export type AnatomyCardType = 'character' | 'action' | 'item' | 'location';

export type AnatomyHotspot = {
  /** Stable id, used as the section anchor (e.g. #learn/anatomy/lore) */
  id: string;
  label: string;
  /** Position on the card, as percentages of the schematic box (0-100). */
  x: number;
  y: number;
  /** Optional size override (% of schematic). Defaults to 12 x 8. */
  w?: number;
  h?: number;
  shortHint: string;
  detail: string;
};

export type AnatomyCard = {
  type: AnatomyCardType;
  title: string;
  subtitle: string;
  hotspots: AnatomyHotspot[];
};

const COMMON_TOP: AnatomyHotspot[] = [
  {
    id: 'cost',
    label: 'Ink Cost',
    x: 8,
    y: 6,
    w: 10,
    h: 8,
    shortHint: 'How much ink the card costs to play.',
    detail:
      'The hexagon in the top-left shows the ink cost. To play this card you must exert that many cards in your inkwell.',
  },
  {
    id: 'inkable',
    label: 'Inkable Border',
    x: 50,
    y: 50,
    w: 90,
    h: 88,
    shortHint: 'Silver swirl = can be inked. Plain = cannot.',
    detail:
      'Look at the outer border. A glowing silver swirl means the card is inkable and can be placed face-down in your inkwell as a resource. A plain border means the card can only be played, never used as ink.',
  },
  {
    id: 'name',
    label: 'Name & Version',
    x: 50,
    y: 60,
    w: 70,
    h: 8,
    shortHint: 'The card name and its version subtitle.',
    detail:
      'Lorcana cards share names with multiple versions (for example, "Mickey Mouse — Brave Little Tailor"). You can have up to 4 copies of the same exact name + version in your deck.',
  },
  {
    id: 'type',
    label: 'Card Type',
    x: 50,
    y: 72,
    w: 60,
    h: 5,
    shortHint: 'Character, Action, Item, or Location.',
    detail:
      'Card type determines how the card works. Characters quest and challenge, Actions resolve once, Items stay in play with passive effects, and Locations are places your characters can move to.',
  },
  {
    id: 'abilities',
    label: 'Ability Text',
    x: 50,
    y: 84,
    w: 86,
    h: 18,
    shortHint: 'Keywords and rules text for the card.',
    detail:
      'Keywords (like Rush, Bodyguard, Evasive, Ward, Resist) and any unique abilities live here. Keywords are shortcuts for common rules — tap one to learn more in the rules guide.',
  },
];

export const ANATOMY_CARDS: Record<AnatomyCardType, AnatomyCard> = {
  character: {
    type: 'character',
    title: 'Character',
    subtitle: 'Quests for lore and can challenge other characters.',
    hotspots: [
      ...COMMON_TOP,
      {
        id: 'strength',
        label: 'Strength',
        x: 18,
        y: 92,
        w: 12,
        h: 8,
        shortHint: 'Damage dealt in a challenge.',
        detail:
          'The red sword icon. When this character challenges or is challenged, both characters deal damage equal to their strength.',
      },
      {
        id: 'willpower',
        label: 'Willpower',
        x: 50,
        y: 92,
        w: 12,
        h: 8,
        shortHint: 'Damage the character can take before being banished.',
        detail:
          'The blue shield icon. A character is banished when damage on them is equal to or greater than their willpower.',
      },
      {
        id: 'lore',
        label: 'Lore Value',
        x: 82,
        y: 92,
        w: 12,
        h: 8,
        shortHint: 'Lore earned per quest.',
        detail:
          'The gold teardrop. Exert a ready character to quest and gain this much lore. First player to 20 lore wins.',
      },
    ],
  },
  action: {
    type: 'action',
    title: 'Action',
    subtitle: 'A one-time effect that resolves immediately.',
    hotspots: [
      ...COMMON_TOP.filter((h) => h.id !== 'abilities').map((h) =>
        h.id === 'type' ? { ...h, detail: 'Actions resolve immediately and go to the discard pile. Some Actions are "Songs" — they can also be played by exerting a character with cost equal to or higher than the song.' } : h,
      ),
      {
        id: 'effect',
        label: 'Effect Text',
        x: 50,
        y: 86,
        w: 86,
        h: 22,
        shortHint: 'What the action does when played.',
        detail:
          'Actions have a single block of rules text that resolves once. If the action is a Song, the song banner appears above the text.',
      },
    ],
  },
  item: {
    type: 'item',
    title: 'Item',
    subtitle: 'Stays in play with ongoing or activated effects.',
    hotspots: [
      ...COMMON_TOP.map((h) =>
        h.id === 'type' ? { ...h, detail: 'Items stay in play after you play them. Many items have an activated ability you can trigger by exerting the item, sometimes paired with an ink cost.' } : h,
      ),
    ],
  },
  location: {
    type: 'location',
    title: 'Location',
    subtitle: 'A place characters can move to and quest from.',
    hotspots: [
      ...COMMON_TOP,
      {
        id: 'move-cost',
        label: 'Move Cost',
        x: 18,
        y: 92,
        w: 14,
        h: 8,
        shortHint: 'Ink to move a character here.',
        detail:
          'Pay this much ink to move one of your characters to this location. While a character is at a location, they are considered to be there until they move or it is banished.',
      },
      {
        id: 'loc-willpower',
        label: 'Willpower',
        x: 50,
        y: 92,
        w: 12,
        h: 8,
        shortHint: 'Damage the location can take.',
        detail:
          'Locations can be challenged. The blue shield is their willpower — when damage reaches it, the location is banished.',
      },
      {
        id: 'loc-lore',
        label: 'Lore Per Turn',
        x: 82,
        y: 92,
        w: 12,
        h: 8,
        shortHint: 'Lore the location earns each turn.',
        detail:
          'At the start of your turn, gain this much lore for each of your locations in play. Locations earn lore automatically — no questing required.',
      },
    ],
  },
};

export const ANATOMY_CARD_ORDER: AnatomyCardType[] = ['character', 'action', 'item', 'location'];

/** Lesson keys saved to learn_progress per hotspot, namespaced by card type. */
export function anatomyLessonKey(type: AnatomyCardType, hotspotId: string) {
  return `anatomy-${type}-${hotspotId}`;
}
